import { FormHeader, FormRow, InputContainer } from '@creditornot/cb-form';
import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import Decimal from 'decimal.js';
import React from 'react';
import { Field } from 'react-final-form';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import * as yup from 'yup';

import { RFFFieldErrorWrapper } from 'common/components';
import InputNumber from 'common/components/InputNumber';
import { Typography as T } from 'common/typography';

export type FormValues = {
  homedeliveryRelativeCommission?: number;
  takeawayRelativeCommission?: number;
  eatinRelativeCommission?: number;
};

type Props = {
  disabled: boolean;
};

const Root = styled.div`
  margin-block-start: 32px;
`;

const Header = styled(T.Title1)`
  color: ${cbSemanticCssVars.textPrimary};
`;

const StyledInputContainer = styled(InputContainer)`
  max-width: 392px;
  &:not(:first-child) {
    margin-block-start: 16px;
  }
`;

const yupTestUpTo2DecimalPlaces = {
  name: 'upTo2DecimalPlaces',
  message: { id: 'validation.atMostNDecimals', values: { decimals: 2 } },
  test: (value: number | undefined) => value === undefined || new Decimal(value).decimalPlaces() <= 2,
};

export const commissionsFormSchema: yup.SchemaOf<FormValues> = yup.object({
  homedeliveryRelativeCommission: yup.number().min(0).max(100).test(yupTestUpTo2DecimalPlaces),
  takeawayRelativeCommission: yup.number().min(0).max(100).test(yupTestUpTo2DecimalPlaces),
  eatinRelativeCommission: yup.number().min(0).max(100).test(yupTestUpTo2DecimalPlaces),
});

const CommissionsFormSection = ({ disabled }: Props) => {
  const intl = useIntl();
  return (
    <Root>
      <FormHeader>
        <Header>
          <FormattedMessage id="menu-item.commissions" />
        </Header>
      </FormHeader>

      <FormRow label={intl.formatMessage({ id: 'menu-item.custom-commission' })}>
        <Field<number> name="homedeliveryRelativeCommission">
          {field => (
            <RFFFieldErrorWrapper field={field}>
              {input => (
                <StyledInputContainer
                  label={intl.formatMessage({ id: 'delivery-types.homedelivery' })}
                  labelHtmlFor="delivery-types.homedelivery"
                >
                  <InputNumber
                    {...input}
                    disabled={disabled}
                    placeholder={intl.formatMessage({ id: 'menu-item.default-commission' })}
                    suffixText="%"
                    id="delivery-types.homedelivery"
                  />
                </StyledInputContainer>
              )}
            </RFFFieldErrorWrapper>
          )}
        </Field>
        <Field<number> name="takeawayRelativeCommission">
          {field => (
            <RFFFieldErrorWrapper field={field}>
              {input => (
                <StyledInputContainer label={intl.formatMessage({ id: 'delivery-types.takeaway' })}>
                  <InputNumber
                    {...input}
                    disabled={disabled}
                    placeholder={intl.formatMessage({ id: 'menu-item.default-commission' })}
                    suffixText="%"
                  />
                </StyledInputContainer>
              )}
            </RFFFieldErrorWrapper>
          )}
        </Field>
        <Field<number> name="eatinRelativeCommission">
          {field => (
            <RFFFieldErrorWrapper field={field}>
              {input => (
                <StyledInputContainer label={intl.formatMessage({ id: 'delivery-types.eatin' })}>
                  <InputNumber
                    {...input}
                    disabled={disabled}
                    placeholder={intl.formatMessage({ id: 'menu-item.default-commission' })}
                    suffixText="%"
                  />
                </StyledInputContainer>
              )}
            </RFFFieldErrorWrapper>
          )}
        </Field>
      </FormRow>
    </Root>
  );
};

export default CommissionsFormSection;
